import { Switch } from '@thanx/uikit/switch'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { useGetInsightsLabQuery } from 'api/insightsLab'
import PageHeader from 'components/PageHeader'
import PopoverInfo from 'components/PopoverInfo'
import SearchablePaginatedContent from 'components/SearchablePaginatedContent'
import SideNav from 'components/SideNav'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useFlag from 'hooks/useFlag'
import useFlagsReady from 'hooks/useFlagsReady'
import usePaginateArray from 'hooks/usePaginateArray'
import { buildTranslate } from 'locales'
import { useMemo, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap-five'
import InsightsLabBanner from './InsightsLabBanner'
import ReportItem from './ReportItem'
import Workspace from './v2/Workspace'

const t = buildTranslate('insights_lab')

const InsightsLab = () => {
  const [css] = useStyletron()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const merchant = useCurrentMerchant()
  // v2 keeps this page's header + banner and swaps only the content below it for
  // the in-page multi-tab report workspace. Default OFF — rollout is controlled
  // in LaunchDarkly.
  const insightsLabV2Enabled = useFlag('insights-lab-v2', false)
  const flagsReady = useFlagsReady()

  // The v1 report list is a slow Looker folder walk that v2's Workspace never
  // reads, so only request it once the merchant's flags say v1 renders.
  const { data: insightsLab = [], isLoading } = useGetInsightsLabQuery(
    undefined,
    {
      skip: !merchant || !flagsReady || insightsLabV2Enabled,
    }
  )

  // Get all unique categories with counts
  const categories = useMemo(() => {
    return Array.from(
      new Set(insightsLab.map(item => item.category)).values()
    ).sort((a, b) => a.localeCompare(b))
  }, [insightsLab])

  const filteredInsights = useMemo(() => {
    let results = [...insightsLab]

    // Apply category filter
    if (
      selectedCategory &&
      selectedCategory !== 'all' &&
      searchQuery.trim() === ''
    ) {
      results = results.filter(item => item.category === selectedCategory)
    }

    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      results = results.filter(
        item =>
          item.title.toLowerCase().includes(query) ||
          item.description.toLowerCase().includes(query)
      )
    }

    return results
  }, [insightsLab, selectedCategory, searchQuery])

  const { currentPage, setPage, paginatedData, totalPages } = usePaginateArray(
    filteredInsights,
    15
  )

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    setPage(1)
  }

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category)
    setPage(1)
  }

  if (!merchant) return null

  // v2 moves the "content may change" disclaimer under the subtitle in the
  // header (full note in a hover tooltip on the trailing icon). v1 keeps
  // the original treatment: a plain subtitle here + the <InsightsLabBanner />
  // tip card above the report list (rendered below). Gated so the flag-off
  // experience stays unchanged.
  const headerDescription = insightsLabV2Enabled ? (
    <>
      <Text.BodyText3 tag="p" className="mt-xs mb-xs">
        {t('view.subtitle')}
      </Text.BodyText3>
      <Text.BodyText5
        tag="p"
        color="grey70"
        className="mt-0 mb-m d-flex align-items-center"
      >
        {t('view.disclaimer')}
        <PopoverInfo
          className="ml-xs"
          placement="top"
          text={t('view.disclaimer_tooltip')}
        />
      </Text.BodyText5>
    </>
  ) : (
    t('view.subtitle')
  )

  // Gated like headerDescription above: the flag-off page keeps its
  // original title rather than picking up v2's "All reports" rename.
  const headerTitle = insightsLabV2Enabled
    ? t('view.title_v2')
    : t('view.title')

  return (
    <PageHeader title={headerTitle} description={headerDescription}>
      <div
        className={`w-100 grey-05-bkg border-top-1 grey-20-border ${css({
          minHeight: '100vh',
        })}`}
      >
        <Container className="full-height pb-xl">
          {!insightsLabV2Enabled && <InsightsLabBanner />}
          {insightsLabV2Enabled ? (
            <Workspace />
          ) : (
            <SearchablePaginatedContent
              contentIsLoading={isLoading || !flagsReady}
              onChangePage={setPage}
              onChangeSearchInput={handleSearchChange}
              currentPage={currentPage}
              numberOfPages={totalPages}
            >
              <Row className="mt-xl" style={{ scrollMarginTop: '75px' }}>
                <Switch condition={searchQuery.trim() === ''}>
                  <Col data-testid="category-filter" md={3} className="pr-l">
                    <SideNav
                      items={categories}
                      current={selectedCategory || ''}
                      all={t('categories.all')}
                      onClick={handleCategorySelect}
                    />
                  </Col>
                </Switch>
                <Col>
                  <Switch condition={searchQuery.trim() === ''}>
                    <Row>
                      <Col>
                        <Text.Header3 className="mt-0 mb-s">
                          {selectedCategory === 'all'
                            ? t('categories.all')
                            : selectedCategory || t('other')}
                        </Text.Header3>
                      </Col>
                    </Row>
                  </Switch>

                  {paginatedData.map(item => (
                    <ReportItem key={item.id} {...item} />
                  ))}
                </Col>
              </Row>
            </SearchablePaginatedContent>
          )}
        </Container>
      </div>
    </PageHeader>
  )
}

export default InsightsLab
